import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ALL_STATUS_OPTIONS,
  getStatusDropdownLabel,
} from '../../utils/candidateStatus';

function StatusSelect({
  value = '',
  onChange,
  placeholder = 'Select Status',
  className = '',
  id,
  leadingOptions = [],
}) {
  const rootRef = useRef(null);
  const searchRef = useRef(null);
  const optionsRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [highlightIndex, setHighlightIndex] = useState(0);

  const options = useMemo(
    () => [
      ...leadingOptions.map((option) => ({
        value: option.value,
        label: option.label || getStatusDropdownLabel(option.value),
      })),
      ...ALL_STATUS_OPTIONS.map((status) => ({
        value: status,
        label: getStatusDropdownLabel(status),
      })),
    ],
    [leadingOptions],
  );

  const filteredOptions = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return options;
    return options.filter(
      (option) => option.label.toLowerCase().includes(query)
        || option.value.toLowerCase().includes(query),
    );
  }, [options, search]);

  const selectedLabel = value ? getStatusDropdownLabel(value) : placeholder;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (open) {
      setSearch('');
      setHighlightIndex(0);
      setTimeout(() => searchRef.current?.focus(), 0);
    }
  }, [open]);

  useEffect(() => {
    if (highlightIndex >= filteredOptions.length) {
      setHighlightIndex(0);
    }
  }, [filteredOptions.length, highlightIndex]);

  useEffect(() => {
    if (!open || !optionsRef.current) return;
    const highlighted = optionsRef.current.querySelector('.hr-status-select-option.is-highlighted');
    highlighted?.scrollIntoView({ block: 'nearest' });
  }, [highlightIndex, open, filteredOptions.length]);

  const handleSelect = (option) => {
    onChange?.({ target: { value: option.value } });
    setOpen(false);
  };

  const handleKeyDown = (event) => {
    if (!open) {
      if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        setOpen(true);
      }
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      setOpen(false);
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlightIndex((prev) => Math.min(prev + 1, filteredOptions.length - 1));
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlightIndex((prev) => Math.max(prev - 1, 0));
      return;
    }

    if (event.key === 'Enter' && filteredOptions[highlightIndex]) {
      event.preventDefault();
      handleSelect(filteredOptions[highlightIndex]);
    }
  };

  return (
    <div
      ref={rootRef}
      className={`hr-status-select ${open ? 'is-open' : ''} ${className}`.trim()}
      onKeyDown={handleKeyDown}
    >
      <button
        type="button"
        id={id}
        className="hr-status-select-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
      >
        <span className="hr-status-select-value">{selectedLabel}</span>
        <span className="hr-status-select-caret" aria-hidden>▾</span>
      </button>

      {open && (
        <div className="hr-status-select-menu" role="listbox">
          <input
            ref={searchRef}
            type="search"
            className="hr-status-select-search"
            placeholder="Search status..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setHighlightIndex(0);
            }}
          />
          <div ref={optionsRef} className="hr-status-select-options">
            {filteredOptions.length === 0 ? (
              <div className="hr-status-select-empty">No statuses found</div>
            ) : (
              filteredOptions.map((option, index) => (
                <button
                  key={option.value}
                  type="button"
                  role="option"
                  aria-selected={option.value === value}
                  className={[
                    'hr-status-select-option',
                    index === highlightIndex ? 'is-highlighted' : '',
                    option.value === value ? 'is-selected' : '',
                  ].filter(Boolean).join(' ')}
                  onMouseEnter={() => setHighlightIndex(index)}
                  onClick={() => handleSelect(option)}
                >
                  {option.label}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default StatusSelect;
